import * as yup from "yup";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  useForm,
  UseFormRegister,
  UseFormWatch,
  Path,
  FieldError,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  PrimaryButton,
  TextInput,
  CheckBox,
  OutlinedButton,
  MultipleSelectOption,
  MultipleSelect,
} from "@normasteaching/norma-ui";
import { PropsWithChildren, useState, useEffect, useMemo } from "react";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import {
  buildPaymentMethodsValidationSchema,
  getAvailablePaymentMethods,
  getPaymentMethodConfig,
} from "../../NormasTeachingCheckout/PaymentMethodRegistryUtils.ts";
import {
  PaymentMethod,
  getPaymentMethodName,
} from "../../NormasTeachingCheckout/PaymentMethod.ts";
import { PaymentMethodConfigsDTO } from "../../NormasTeachingCheckout/PaymentMethodConfiguration.ts";
import { Plus, Trash2, Edit2, X } from "lucide-react";
import { NormasTeachingBundleDTO } from "../../ProductService/NormasTeachingBundle/useNormasTeachingBundleById.ts";

export type CreateNormasTeachingBundleFormValues = {
  bundleName: string;
  variantBundleName: string;
  priceInEuros: number;
  purchaseAcTags?: string[];
  refundAcTags?: string[];
  bundleProducts: {
    products: { id: string; priceInEuros: number }[];
  }[];
  selectedPaymentMethods: PaymentMethod[];
  bundleDescription?: string;
  analyticsTag?: string;
  isCouponEnabled?: boolean;
} & Partial<PaymentMethodConfigsDTO>;

const createValidationSchema = (
  selectedMethods: PaymentMethod[],
): yup.AnyObjectSchema => {
  const baseSchema = yup.object({
    bundleName: yup.string().required("Inserire un nome del bundle"),
    variantBundleName: yup
      .string()
      .required("Inserire un nome interno del bundle"),
    priceInEuros: yup
      .number()
      .required("Inserire un prezzo valido")
      .min(1, "Inserire un prezzo > 0")
      .typeError("Inserire un prezzo valido"),
    purchaseAcTags: yup.array().of(yup.string().required()),
    refundAcTags: yup.array().of(yup.string().required()),
    bundleProducts: yup
      .array()
      .of(
        yup
          .object({
            products: yup
              .array()
              .of(
                yup.object({
                  id: yup.string().required("Inserire un ID del prodotto"),
                  priceInEuros: yup
                    .number()
                    .required("Inserire un prezzo valido")
                    .typeError("Inserire un prezzo valido"),
                }),
              )
              .min(1, "Inserire almeno un prodotto nel bundle")
              .required(),
          })
          .required(),
      )
      .min(1, "Inserisci almeno una persona nel bundle"),
    selectedPaymentMethods: yup
      .array()
      .of(yup.string().oneOf(Object.values(PaymentMethod)))
      .min(1, "Seleziona almeno un metodo di pagamento")
      .required("Seleziona almeno un metodo di pagamento"),
    analyticsTag: yup.string().optional(),
    bundleDescription: yup.string().optional(),
    isCouponEnabled: yup.boolean().default(false),
  });

  const paymentMethodsSchema =
    buildPaymentMethodsValidationSchema(selectedMethods);

  return baseSchema.concat(
    paymentMethodsSchema as yup.AnyObjectSchema,
  ) as yup.AnyObjectSchema;
};

function InputWithLabel({
  children,
  label,
}: PropsWithChildren<{ label: string }>) {
  return (
    <div className={"flex flex-col gap-1 flex-1"}>
      <label className={"font-medium"}>{label}</label>
      {children}
    </div>
  );
}

function bundleToFormValues(
  bundle: NormasTeachingBundleDTO,
): Partial<CreateNormasTeachingBundleFormValues> {
  const formValues: Partial<CreateNormasTeachingBundleFormValues> = {
    bundleName: bundle.bundleName,
    variantBundleName: bundle.variantBundleName,
    priceInEuros: bundle.priceInCents.amount / 100,
    purchaseAcTags: bundle.purchaseAcTags || [],
    refundAcTags: bundle.refundAcTags || [],
    analyticsTag: bundle.analyticsTag,
    bundleDescription: bundle.bundleDescription,
    isCouponEnabled: bundle.isCouponEnabled ?? false,
  };

  // Converti bundleProducts
  if (bundle.bundleProducts) {
    formValues.bundleProducts = bundle.bundleProducts.map((bundleProduct) => ({
      products: bundleProduct.products.map((product) => ({
        id: product.id,
        priceInEuros: product.priceInCents.amount / 100,
      })),
    }));
  }

  // Payment Methods
  const selectedMethods: PaymentMethod[] = [];

  if (bundle.stripeConfig) {
    selectedMethods.push(PaymentMethod.Stripe);
    formValues.stripeConfig = bundle.stripeConfig;
  }

  if (bundle.heylightConfig) {
    selectedMethods.push(PaymentMethod.HeyLight);
    formValues.heylightConfig = bundle.heylightConfig;
  }

  formValues.selectedPaymentMethods = selectedMethods;

  return formValues;
}

function ProductSelectionModal({
  isOpen,
  onClose,
  products,
  selectedProducts,
  onSave,
}: ProductSelectionModalProps) {
  const [localProducts, setLocalProducts] =
    useState<ProductWithPrice[]>(selectedProducts);

  useEffect(() => {
    setLocalProducts(selectedProducts);
  }, [selectedProducts, isOpen]);

  if (!isOpen) return null;

  const availableProducts = products.filter(
    (p) => !localProducts.some((lp) => lp.id === p._id),
  );
  const addProduct = (productId: string, productPrice: number) => {
    setLocalProducts([
      ...localProducts,
      { id: productId, priceInEuros: productPrice / 100 },
    ]);
  };

  const removeProduct = (productId: string) => {
    setLocalProducts(localProducts.filter((p) => p.id !== productId));
  };

  const updatePrice = (productId: string, price: number) => {
    setLocalProducts(
      localProducts.map((p) =>
        p.id === productId ? { ...p, priceInEuros: price } : p,
      ),
    );
  };

  const handleSave = () => {
    onSave(localProducts);
    onClose();
  };

  const getProductName = (productId: string) => {
    return (
      `${products.find((p) => p._id === productId)?.productName} - ` +
        `${products.find((p) => p._id === productId)?.variantProductName}` ||
      "Prodotto sconosciuto"
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-bold">Seleziona Prodotti e Prezzi</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {localProducts.length > 0 && (
            <div className="space-y-3 mb-6">
              {localProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {getProductName(product.id)}
                    </p>
                  </div>
                  <div className="w-32">
                    <TextInput
                      onWheel={(e) => {
                        const el = e.target as HTMLInputElement;
                        el?.blur();
                        e.stopPropagation();
                        setTimeout(() => el?.focus(), 0);
                      }}
                      onChange={(e) =>
                        updatePrice(product.id, parseFloat(e.target.value) || 0)
                      }
                      type={"number"}
                      min={0}
                      placeholder="Prezzo (€)"
                      value={product.priceInEuros}
                    />
                  </div>
                  <button
                    onClick={() => removeProduct(product.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {availableProducts.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                Prodotti disponibili:
              </p>
              <div className="space-y-2">
                {availableProducts.map((product) => (
                  <button
                    key={product._id}
                    onClick={() =>
                      addProduct(product._id, product.priceInCents.amount)
                    }
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <p className="font-medium">
                      {product.productName} - {product.variantProductName}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {availableProducts.length === 0 && localProducts.length === 0 && (
            <p className="text-center text-gray-500 py-8">
              Nessun prodotto disponibile
            </p>
          )}
        </div>

        <div className="p-6 border-t flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Annulla
          </button>
          <PrimaryButton label="Salva" onClick={handleSave} />
        </div>
      </div>
    </div>
  );
}

interface ProductWithPrice {
  id: string;
  priceInEuros: number;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: NormasTeachingProductDTO[];
  selectedProducts: ProductWithPrice[];
  onSave: (products: ProductWithPrice[]) => void;
}

interface BundlePersonSelectorProps<
  TFormValues extends FieldValues = FieldValues,
> {
  products: NormasTeachingProductDTO[];
  control: Control<TFormValues>;
  errors: FieldErrors<TFormValues>;
  fieldName: string;
}

export function BundlePersonSelector<TFormValues extends FieldValues>({
  products,
  control,
  fieldName,
  errors,
}: BundlePersonSelectorProps<TFormValues>) {
  return (
    <Controller
      name={fieldName as Path<TFormValues>}
      control={control}
      render={({ field }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [modalState, setModalState] = useState<{
          isOpen: boolean;
          slotIndex: number | null;
        }>({ isOpen: false, slotIndex: null });

        const slots: { products: ProductWithPrice[] }[] = field.value || [];

        const addSlot = () => {
          field.onChange([...slots, { products: [] }]);
        };

        const removeSlot = (index: number) => {
          field.onChange(slots.filter((_, i) => i !== index));
        };

        const updateSlotProducts = (
          index: number,
          products: ProductWithPrice[],
        ) => {
          const updatedSlots = slots.map((slot, i) => {
            if (i === index) {
              return { ...slot, products };
            }
            return slot;
          });
          field.onChange(updatedSlots);
        };

        const openModal = (index: number) => {
          setModalState({ isOpen: true, slotIndex: index });
        };

        const closeModal = () => {
          setModalState({ isOpen: false, slotIndex: null });
        };

        const handleSaveProducts = (products: ProductWithPrice[]) => {
          if (modalState.slotIndex !== null) {
            updateSlotProducts(modalState.slotIndex, products);
          }
        };

        const getProductName = (productId: string) => {
          return (
            `${products.find((p) => p._id === productId)?.productName} - ${products.find((p) => p._id === productId)?.variantProductName}` ||
            "Prodotto sconosciuto"
          );
        };

        const calculateSlotTotal = (slotProducts: ProductWithPrice[]) => {
          return slotProducts
            .reduce((sum, p) => sum + p.priceInEuros, 0)
            .toFixed(2);
        };

        const bundleProductsErrors = errors[fieldName as keyof TFormValues] as
          | (FieldError & FieldErrors<{ products: ProductWithPrice[] }[]>)
          | undefined;

        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-gray-800">
                  Persone nel Bundle
                </label>
                <p className="text-sm text-gray-600">
                  Aggiungi le persone e seleziona i prodotti con i relativi
                  prezzi
                </p>
              </div>
              <div>
                <PrimaryButton
                  iconLeft={<Plus size={18} />}
                  label={"Aggiungi Persona"}
                  onClick={addSlot}
                />
              </div>
            </div>

            {slots.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 text-sm">
                  Nessuna persona aggiunta. Clicca "Aggiungi Persona" per
                  iniziare.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {slots.map((slot, index) => {
                  const slotError = bundleProductsErrors?.[index]?.products;
                  const slotTotal = calculateSlotTotal(slot.products);

                  return (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-700">
                          Persona {index + 1}
                        </h3>
                        <button
                          type="button"
                          onClick={() => removeSlot(index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700">
                            Prodotti Assegnati
                          </label>
                          <div>
                            <OutlinedButton
                              iconLeft={<Edit2 size={16} />}
                              label={
                                slot.products.length > 0
                                  ? "Modifica"
                                  : "Aggiungi Prodotti"
                              }
                              onClick={() => openModal(index)}
                            />
                          </div>
                        </div>

                        {slot.products.length > 0 ? (
                          <div className="bg-white rounded-lg p-3 space-y-2">
                            {slot.products.map((product) => (
                              <div
                                key={product.id}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-700">
                                  {getProductName(product.id)}
                                </span>
                                <span className="font-medium">
                                  €{product.priceInEuros.toFixed(2)}
                                </span>
                              </div>
                            ))}
                            <div className="pt-2 border-t flex items-center justify-between font-bold">
                              <span>Totale Persona {index + 1}</span>
                              <span>€{slotTotal}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white rounded-lg p-4 text-center text-gray-500 text-sm">
                            Nessun prodotto aggiunto
                          </div>
                        )}

                        {slotError?.message && (
                          <p className="text-red-600 text-sm">
                            {slotError.message}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {bundleProductsErrors?.message && (
              <p className="text-red-600 text-sm mt-2">
                {bundleProductsErrors.message}
              </p>
            )}

            {modalState.isOpen && modalState.slotIndex !== null && (
              <ProductSelectionModal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                products={products}
                selectedProducts={slots[modalState.slotIndex]?.products || []}
                onSave={handleSaveProducts}
              />
            )}
          </div>
        );
      }}
    />
  );
}

export function CreateNormasTeachingBundleForm({
  initialKnownPurchaseAcTags,
  initialKnownRefundAcTags,
  products,
  creating,
  onSubmit,
  initialBundle,
  isEditMode = false,
}: {
  initialKnownPurchaseAcTags: string[];
  initialKnownRefundAcTags: string[];
  initialKnownAnalyticsTags: string[];
  products: NormasTeachingProductDTO[];
  onSubmit: (data: CreateNormasTeachingBundleFormValues) => void;
  creating: boolean;
  initialBundle?: NormasTeachingBundleDTO;
  isEditMode?: boolean;
}) {
  function mapStringsToMultipleSelectOptions(
    strings: string[],
  ): MultipleSelectOption<string>[] {
    return strings.map((s) => ({
      id: s,
      label: s,
    }));
  }

  const [acPurchaseTagOptions, setAcPurchaseTagOptions] = useState<
    MultipleSelectOption<string>[]
  >(mapStringsToMultipleSelectOptions(initialKnownPurchaseAcTags));
  const [acRefundTagOptions, setAcRefundTagOptions] = useState<
    MultipleSelectOption<string>[]
  >(mapStringsToMultipleSelectOptions(initialKnownRefundAcTags));

  const [selectedMethods, setSelectedMethods] = useState<PaymentMethod[]>([]);

  const defaultValues = initialBundle
    ? bundleToFormValues(initialBundle)
    : {
        bundleProducts: [],
        selectedPaymentMethods: [],
      };

  const {
    control,
    register,
    setValue,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<CreateNormasTeachingBundleFormValues>({
    resolver: yupResolver(createValidationSchema(selectedMethods)),
    defaultValues: defaultValues,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedPaymentMethods = watch("selectedPaymentMethods") || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bundleProducts = watch("bundleProducts") || [];

  const computedAnalyticsTag = useMemo(() => {
    const allProductIds = bundleProducts.flatMap((slot) =>
      slot.products.map((p) => p.id),
    );

    const uniqueProductIds = Array.from(new Set(allProductIds));

    if (uniqueProductIds.length === 0) {
      return "";
    }

    const analyticsTags = uniqueProductIds
      .map((productId) => {
        const product = products.find((p) => p._id === productId);
        return product?.analyticsTag;
      })
      .filter((tag): tag is string => !!tag);

    if (analyticsTags.length === 0) {
      return "";
    }

    return `Bundle - ${analyticsTags.join(" - ")}`;
  }, [bundleProducts, products]);

  useEffect(() => {
    setSelectedMethods(selectedPaymentMethods);
  }, [selectedPaymentMethods]);

  useEffect(() => {
    if (isEditMode && bundleProducts.length === 0) {
      return;
    }

    const totalPrice = bundleProducts.reduce((total, slot) => {
      const slotTotal = slot.products.reduce(
        (sum, product) => sum + (product.priceInEuros || 0),
        0,
      );
      return total + slotTotal;
    }, 0);

    setValue("priceInEuros", totalPrice);
  }, [bundleProducts, setValue, isEditMode]);

  useEffect(() => {
    setValue("analyticsTag", computedAnalyticsTag);
  }, [computedAnalyticsTag, setValue]);

  const onSubmitInternal = async (
    data: CreateNormasTeachingBundleFormValues,
  ) => {
    onSubmit(data);
  };

  return (
    <form className={"flex flex-col gap-4"}>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Nome del Bundle"}>
          <TextInput
            {...register("bundleName")}
            errorMessage={errors?.bundleName?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"Nome variante Bundle"}>
          <TextInput
            {...register("variantBundleName")}
            errorMessage={errors?.variantBundleName?.message}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Active Campaign tag post acquisto"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.purchaseAcTags?.message}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={acPurchaseTagOptions}
                  allowCreateInfo={{
                    setOptions: setAcPurchaseTagOptions,
                  }}
                />
              );
            }}
            name={"purchaseAcTags"}
            control={control}
          />
        </InputWithLabel>
        <InputWithLabel label={"Active Campaign tag post refund"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.refundAcTags?.message}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={acRefundTagOptions}
                  allowCreateInfo={{
                    setOptions: setAcRefundTagOptions,
                  }}
                />
              );
            }}
            name={"refundAcTags"}
            control={control}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <div className="flex flex-1 gap-4">
          <InputWithLabel label={"Prezzo (€)"}>
            <TextInput
              disabled={true}
              errorMessage={errors?.priceInEuros?.message}
              onWheel={(e) => {
                const el = e.target as HTMLInputElement;
                el?.blur();
                e.stopPropagation();
                setTimeout(() => {
                  el?.focus();
                }, 0);
              }}
              type={"number"}
              min={0}
              {...register("priceInEuros")}
            />
          </InputWithLabel>
        </div>
      </div>

      <BundlePersonSelector
        products={products}
        fieldName="bundleProducts"
        control={control}
        errors={errors}
      />

      <div>
        <InputWithLabel label={"Analytics tag"}>
          <TextInput
            disabled={true}
            value={computedAnalyticsTag}
            errorMessage={errors?.analyticsTag?.message}
            // onWheel={(e) => {
            //   const el = e.target as HTMLInputElement;
            //   el?.blur();
            //   e.stopPropagation();
            //   setTimeout(() => {
            //     el?.focus();
            //   }, 0);
            // }}
            {...register("analyticsTag")}
          />
        </InputWithLabel>
      </div>

      <div>
        <InputWithLabel label={"Descrizione Bundle"}>
          <textarea
            {...register("bundleDescription")}
            className="w-full min-h-[150px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Inserisci la descrizione del bundle, visualizzata all'utente in fase di checkout. Supporta Markdown per formattazione e immagini."
          />
          {errors?.bundleDescription?.message && (
            <p className="text-red-600 text-sm mt-1">
              {errors.bundleDescription.message}
            </p>
          )}
        </InputWithLabel>
        <p className="text-sm text-gray-500 mt-1">
          Puoi usare Markdown: **grassetto**, *corsivo*, liste, link e immagini
          con ![alt](url) oppure{" "}
          {`<img src="url" alt="alt" width="400" style="display: block; margin: 0 auto;" />`}
        </p>
      </div>

      <Controller
        render={({ field }) => {
          return (
            <CheckBox
              label={<span>Abilita bundle ai coupon</span>}
              value={field.value ?? false}
              onChange={field.onChange}
            />
          );
        }}
        name={"isCouponEnabled"}
        control={control}
      />

      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-bold mb-4">
          Metodi di pagamento abilitati
        </h3>

        <div className="space-y-3 mb-4">
          {getAvailablePaymentMethods().map((method) => (
            <Controller
              key={method}
              name="selectedPaymentMethods"
              control={control}
              render={({ field }) => {
                const isChecked = field.value?.includes(method) || false;
                const config = getPaymentMethodConfig(method);

                return (
                  <CheckBox
                    label={
                      <div>
                        <span className="font-medium">
                          {getPaymentMethodName(method)}
                        </span>
                      </div>
                    }
                    value={isChecked}
                    onChange={(checked) => {
                      const currentValues = field.value || [];
                      const newMethods = checked
                        ? [...currentValues, method]
                        : currentValues.filter((m) => m !== method);

                      field.onChange(newMethods);

                      if (checked && config) {
                        setValue(
                          config.configFieldName,
                          config.defaultValues || undefined,
                        );
                      } else if (!checked && config) {
                        setValue(config.configFieldName, undefined);
                      }
                    }}
                  />
                );
              }}
            />
          ))}
        </div>

        {errors.selectedPaymentMethods && (
          <p className="text-red-600 text-sm mb-4">
            {errors.selectedPaymentMethods.message}
          </p>
        )}

        {/* Renderizza i campi di configurazione per ogni metodo selezionato */}
        {selectedPaymentMethods.length > 0 && (
          <div className="space-y-6 mt-6">
            {selectedPaymentMethods.map((method) => {
              const config = getPaymentMethodConfig(method);
              if (!config) return null;

              const ConfigComponent = config.ConfigComponent;

              return (
                <div key={method} className="border-t pt-4">
                  <h4 className="font-semibold mb-3">
                    Configurazione {getPaymentMethodName(method)}
                  </h4>
                  <ConfigComponent
                    fieldName={config.configFieldName}
                    control={control as unknown as Control<FieldValues>}
                    errors={errors}
                    register={
                      register as unknown as UseFormRegister<FieldValues>
                    }
                    watch={watch as unknown as UseFormWatch<FieldValues>}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PrimaryButton
        loading={creating}
        label={isEditMode ? "Aggiorna" : "Crea"}
        onClick={handleSubmit(onSubmitInternal)}
      />
    </form>
  );
}
